<script>
  import Name from './Name.svelte';
  import Type from './Type.svelte';
  import Recipients from './Recipients.svelte';
  import Summary from './Summary.svelte';
  import Design from './Design.svelte';
  import Send from './Send.svelte';
  import { ignore, makeWebsiteRequest, screenshot } from '../Common/tools';
  import { AB_CONTENT, sendTestEmail, validate } from './static';
  import { onMount } from 'svelte';
  import { External, M } from '../external';

  export let data;

  let instance,
    moreInstance,
    tab = 'name',
    defaultEmail = '';
  let cache = {};
  const tabOrder = ['name', 'type', 'design', 'recipients', 'summary', 'send'];

  onMount(function() {

    if (redirectIfNotDraft(data)) {
      return;
    }

    data.domains = [];
    data.statuses = [];

    let settings = External.Website.emailMarketingSettings;
    if (!settings) return;

    settings.domains.forEach(function(row) {
      data.domains.push(row.domain);
      data.statuses.push(row.verificationStatus);
    });

    if (!data.fromName) data.fromName = settings.defaultSender;
    if (!data.fromEmail) data.fromEmail = settings.defaultEmail;
    if (!data.replyTo) data.replyTo = settings.replyEmail || settings.defaultEmail;
  });

  let onChangeTab = async (t) => {
    let nextTab = tabOrder.indexOf(t);
    let currentTab = tabOrder.indexOf(tab);
    let steps = nextTab - currentTab;

    if (nextTab > currentTab) {
      if (steps > 1) {
        for (let i = currentTab; i < nextTab; i++) {
          if (!validate(tabOrder[i], data)) {
            tab = tabOrder[i];
            return;
          }
        }
      }

      if (!validate(tab, data)) {
        return;
      }
    }

    if (!(await checkChangeTab())) {
      return;
    }

    if (tab === 'design' && instance.hasChange()) {
      saveDesignPrompt();
      tab = t;
      return;
    }

    save(false, () => {
      tab = t;
    });
  };

  let checkChangeTab = async () => {
    if (tab === 'design' && !(await instance.verify())) {
      return false;
    }
    return true;
  };

  let next = async () => {
    let index = tabOrder.indexOf(tab);
    if (index === tabOrder.length - 1) {
      return;
    }

    if (!validate(tab, data)) {
      return;
    }

    if (!(await checkChangeTab())) {
      return;
    }

    let screenshot = tab === 'design' && instance.hasChange();
    let newTab = tabOrder[index + 1];
    save(screenshot, () => {
      tab = newTab;
    });
  };

  let previous = async () => {
    let index = tabOrder.indexOf(tab);
    if (index === 0) {
      External.CPanel.goToUrl('/me/crm/marketing/campaigns', true);
      return;
    }

    if (!(await checkChangeTab())) {
      return;
    }

    if (tab === 'design' && instance.hasChange()) {
      saveDesignPrompt();
      tab = tabOrder[index - 1];
      return;
    }

    save(false, () => {
      tab = tabOrder[index - 1];
    });
  };

  let saveDesignPrompt = function() {
    External.CPanel.setChanged(false);
    External.Tools.prompt(
      M.SAVE_CHANGES,
      M.SAVE_DESIGN_CHANGES,
      function() {
        save(true);
      },
      function() {
        let id = External.WebPlatform.getUrlQueryParameter(window.location.href, 'id');

        if (id) {
          External.Tools.makeWebsiteRequest({
            action: 'getMarketingCampaign',
            data: {
              id: id,
              includeContent: true,
              includeStats: false
            },
            onOk: function(response) {
              data.content = response.data.content;
              if (response.data.contentB) {
                data.contentB = response.data.contentB;
              }
            }
          });
        }
      }
    );
  };

  let redirectIfNotDraft = (campaign) => {
    if (campaign.draft === false) {
      External.CPanel.goToUrl('/me/crm/marketing/campaign/overview?id=' + campaign.id, true);
      return true;
    }
    return false;
  };

  let save = function(withDesignChanges, onDone) {
    onDone = onDone == null ? ignore : onDone;

    External.Tools.makeWebsiteRequest({
      action: 'setMarketingCampaign',
      method: 'post',
      data: {
        data: JSON.stringify(data),
        withDesignChanges: withDesignChanges
      },
      onOk: function(response) {
        let newCampaign = response.data;
        if (!newCampaign || newCampaign.id === -1) {
          return;
        }
        if (redirectIfNotDraft(newCampaign)) {
          return;
        }

        External.CPanel.setChanged(false);
        data.id = newCampaign.id;
        (data.data || {}).audience = newCampaign.data ? newCampaign.data.audience : 0;
        data.dataDir = response.data.dataDir;
        let template = External.WebPlatform.getUrlQueryParameter(window.location.href, 'template');
        let type = External.WebPlatform.getUrlQueryParameter(window.location.href, 'type');
        let params = template ? '&template=' + template + (type ? '&type=' + type : '') : '';
        window.history.replaceState({}, document.title, '/me/crm/marketing/campaign?id=' + data.id + params);
        if (withDesignChanges) {
          let content = data.content;
          if (data.type === AB_CONTENT && instance.getSelectedSplit() !== 'a') {
            content = data.contentB ? data.contentB : data.content;
          }

          screenshot({
            campaignId: data.id,
            dimensions: content.style.width,
            content: content
          });
        }
        onDone();
      }
    });
  };

  let saveAndExit = async function() {
    let screenshot = false;
    if (tab === 'design') {
      if (!(await instance.verify())) {
        return;
      }
      screenshot = instance.hasChange();
    }
    save(screenshot, function() {
      External.CPanel.goToUrl('/me/crm/marketing/campaigns', true);
    });
  };

  let changeTab = function(t) {
    tab = t;
  };

  let onDesignChange = () => {
    External.CPanel.setChanged(true);
  };

  let moreOptions = function() {
    const target = External.$(moreInstance);
    const items = [
      { text: M.SAVE_AS_TEMPLATE, icon: 'fa-save', tag: 'save' },
      { text: M.SELECT_ANOTHER_TEMAPLTE, icon: 'fa-file', tag: 'template' },
      { text: M.PREVIEW, icon: 'fa-eye', tag: 'preview' },
      { text: M.SEND_TEST_EMAIL, icon: 'fa-at', tag: 'test' }
    ];

    External.Tools.showTooltipOptions(target, items, async function(target, idx, text, tag) {
      if (tag === 'template') {
        External.Tools.prompt(M.DISCARD_CURRENT_DESIGN, M.DISCARD_CURRENT_DESIGN2, function() {
          if (data.type !== AB_CONTENT) {
            data.content = null;
          } else {
            data[instance.getSelectedSplit() === 'a' ? 'content' : 'contentB'] = null;
          }
        });
        return;
      }

      if (tag === 'save') {
        External.Tools.promptForInput(M.TEMPLATE_NAME, M.TEMPLATE_NAME, '', function(value) {
          instance.saveAsTemplate(value);
        });
        return;
      }

      if (tag === 'preview') {
        if (tab === 'design' && !(await instance.verify())) {
          return;
        }
        let screenshot = tab === 'design' && instance.hasChange();
        save(screenshot, function() {
          let url = 'https://' + External.subdomain + '.' + External.domain + '/me/email?id=' + data.id + '&preview=1';
          if (data.type === AB_CONTENT) {
            url += '&split=' + instance.getSelectedSplit();
          }

          External.Tools.showWebsitePreviewPopup({
            url: url,
            buttonText: M.SHOW_IN_NEW_TAB,
            onButtonAction: function(pageUrl) {
              let win = window.open(pageUrl, '_blank');
              win.focus();
            }
          });
        });
        return;
      }

      if (tag === 'test') {
        save(false, function() {
          sendTestEmail(data, defaultEmail);
        });
      }
    });
  };
</script>

<div class='email-builder-lane flex flex-row align-center flex-none' style='padding: 0 40px;'>
  <div class='flex align-center flex-1'>
    <a class='light-gray-button middle-button circle-effect' on:click={previous}>← {M.PREVIOUS}</a>
  </div>
  <div class='flex align-center flex-none'>
    <ul class='upper-navigation upper-navigation-bottom campaign-navigation'>
      <li><a class:active={tab === "name"} on:click={async () => await onChangeTab("name")}>{M.NAME}</a></li>
      <li><a class:active={tab === "type"} on:click={async () => await onChangeTab("type")}>{M.TYPE}</a></li>
      <li><a class:active={tab === "design"} on:click={async () => await onChangeTab("design")}>{M.DESIGN}</a></li>
      <li><a class:active={tab === "recipients"}
             on:click={async () => await onChangeTab("recipients")}>{M.RECIPIENTS}</a></li>
      <li><a class:active={tab === "summary"} on:click={async () => await onChangeTab("summary")}>{M.SUMMARY_SHORT}</a>
      </li>
      <li><a class:active={tab === "send"} on:click={async () => await onChangeTab("send")}>{M.SEND}</a></li>
    </ul>
  </div>
  <div class='flex align-center flex-1 justify-flex-end'>
    {#if tab === "design" && data.content !== null}
      <a class='middle-button light-gray-button circle-effect mr-5 button-with-icon' on:click={moreOptions}
         bind:this={moreInstance}
      ><span class='material-icons'> more_vert </span>{M.MORE}</a>
    {/if}
    {#if tab !== "send"}
      <a on:click={saveAndExit} class='middle-button light-gray-button circle-effect mr-5'>{M.SAVE_EXIT}</a>
      <a class='blue middle-button' on:click={next}>{M.NEXT} →</a>
    {/if}
  </div>
</div>

{#if tab === "name"}
  <Name bind:data next={next} bind:this={instance} />
{:else if tab === "type"}
  <Type bind:data next={next} bind:this={instance} />
{:else if tab === "design"}
  <Design bind:data bind:this={instance} onChange={onDesignChange} />
{:else if tab === "recipients"}
  <Recipients bind:data next={next} bind:cache bind:this={instance} />
{:else if tab === "summary"}
  <Summary bind:campaign={data} next={next} save={save} changeTab={changeTab} bind:cache bind:this={instance}
           defaultEmail={defaultEmail} />
{:else if tab === "send"}
  <Send bind:campaign={data} bind:this={instance} />
{/if}

<style>
  .more-options-btn {
    margin-right: 15px;
  }
</style>
